/**
 * The message a person sees for a rule refused by `app/src/domain` (T076a). The domain's own error
 * messages stay in French for logs and tests; screens always go through this, so they follow the
 * catalogue (and the person's language, once English exists, T076c).
 */
import { InvalidCellarNameError } from '../domain/profile';
import {
  FutureMonthError,
  InvalidMonthError,
  InvalidPurchaseError,
  InvalidTastingError,
  InvalidWineError,
  RecordChangedError,
  WineIdentityConflictError,
  WineNotFoundError,
  type TypeDeVin,
} from '../domain/cellar';
import { StockError } from '../domain/stock';
import { t, type MessageKey } from './index';

const WINE_FIELD_MESSAGES: Record<InvalidWineError['field'], MessageKey> = {
  domaine: 'error.invalidDomaine',
  cepage: 'error.invalidCepage',
  annee: 'error.invalidAnnee',
  pays: 'error.invalidPays',
  region: 'error.invalidRegion',
  typeDeVin: 'error.invalidTypeDeVin',
  drinkUntil: 'error.invalidDrinkUntil',
};

export function userMessage(error: unknown, fallback: MessageKey = 'error.saveFailed'): string {
  if (error instanceof StockError) return t('error.stockWouldBeNegative');
  if (error instanceof InvalidCellarNameError) return t('error.cellarNameTooLong');
  if (error instanceof FutureMonthError) return t('error.futureMonth');
  if (error instanceof InvalidPurchaseError) {
    return t(
      error.field === 'price'
        ? 'error.invalidPrice'
        : error.field === 'date'
          ? 'error.invalidDate'
          : 'error.invalidQuantity',
    );
  }
  if (error instanceof InvalidTastingError) return t('error.invalidRating');
  if (error instanceof WineNotFoundError) return t('error.wineNotFound');
  if (error instanceof WineIdentityConflictError) return t('error.wineDuplicate');
  if (error instanceof RecordChangedError) return t('error.recordChangedElsewhere');
  if (error instanceof InvalidMonthError) return t('error.invalidMonth');
  if (error instanceof InvalidWineError) return t(WINE_FIELD_MESSAGES[error.field]);
  return t(fallback);
}

/** Rouge, Blanc, Rosé, Vendanges tardives (FR-035). */
export const wineTypeLabel = (type: TypeDeVin): string => t(`wineType.${type}`);
